/**
 * Free drop-in ticket rules (COU-301).
 *
 * A school configures rules that grant free drop-in tickets when a student registers for
 * qualifying activities within a period. Two rule shapes:
 *
 * - `single_activity`: one or more equivalent activities each grant a fixed number of tickets, once
 *   per period per distinct qualifying activity (registering the same activity more than once in a
 *   period does NOT multiply the grant, but multiple distinct activities from the set do).
 * - `bulk`: a set of qualifying activities grants tickets based on how many of them are
 *   registered in the period, via a threshold table (e.g. 2 → 1 ticket, 3 → 2 tickets).
 *
 * "Add-on enabled" for an activity is expressed purely by membership in a rule's activity set;
 * there is no separate flag on `Activity`. An activity may belong to at most one rule
 * (enforced by `validateFreeDropinRules`).
 *
 * NOTE: this is the draft config + pure calculation layer only. It performs no Firestore reads,
 * no ticket creation, and no flow wiring — those are deferred to the integration ticket.
 */

/** When the drop-in tickets are created for a rule. */
export enum FreeDropinTicketTrigger {
  OnRegistration = 'on_registration',
  OnApproval = 'on_approval',
  OnPayment = 'on_payment'
}

export enum FreeDropinRuleType {
  SingleActivity = 'single_activity',
  Bulk = 'bulk'
}

export enum FreeDropinQualificationStatus {
  Qualified = 'qualified',
  Disqualified = 'disqualified'
}

export enum FreeDropinGrantStatus {
  Active = 'active',
  Revoked = 'revoked'
}

/** One row of a bulk rule's threshold table: N qualifying registrations → M free tickets. */
export type FreeDropinTicketThreshold = {
  qualifyingCount: number // number of qualifying registrations required (e.g. 2, 3)
  ticketsGranted: number // number of free drop-in tickets granted at this tier (e.g. 1, 2)
}

type FreeDropinRuleBase = {
  id: string
  name?: string
  packageId: string // reference to Client.dropinPackages[].id
  trigger: FreeDropinTicketTrigger
  isActive?: boolean // undefined or true = active; false = disabled (ignored by calculation)
  /**
   * Whether enabled help dancers earn this rule's reward on the qualifying activities.
   * Undefined or false = excluded (the default). Must be explicitly true to include them.
   */
  includeHelpDancers?: boolean
  /**
   * Revision metadata is optional for legacy persisted rules. New and edited rules must set both
   * fields. `id` remains the logical identity while `revision` identifies immutable snapshots.
   */
  revision?: number
  effectiveFrom?: string
  effectiveUntil?: string
}

/**
 * One or more equivalent qualifying activities; each independently grants `ticketsGranted` once per
 * period (registering the same activity more than once in a period does NOT multiply the grant, but
 * registering multiple distinct activities from the set does — see `qualifyingCountForRule`).
 */
export type SingleActivityFreeDropinRule = FreeDropinRuleBase & {
  ruleType: FreeDropinRuleType.SingleActivity
  activityIds: string[] // the qualifying activity set
  ticketsGranted: number // granted per distinct qualifying activity registered in the period
}

/** A set of qualifying activities grants tickets based on how many are registered in the period. */
export type BulkFreeDropinRule = FreeDropinRuleBase & {
  ruleType: FreeDropinRuleType.Bulk
  activityIds: string[] // qualifying activity set
  /**
   * Complete threshold table. A count below the lowest tier grants 0; a count at or above the
   * highest tier grants the highest tier's amount (no extra beyond the top tier).
   */
  thresholds: FreeDropinTicketThreshold[]
}

export type FreeDropinTicketRule = SingleActivityFreeDropinRule | BulkFreeDropinRule

export type FreeDropinRuleSnapshot = FreeDropinTicketRule & {
  revision: number
  effectiveFrom: string
  capturedAt: string
}

/**
 * One qualifying registration, already reduced from an enrollment/activity. `periodKey` is
 * `activity.periodId || activity.period` (prefers the structured Period id, falls back to the
 * legacy free-text period string), mirroring the `userEnrolledPeriodIds` primitive used by the
 * discount engine.
 */
export type QualifyingRegistration = {
  activityId: string
  periodKey: string
  enrollmentId?: string // traceability only; not used by the calculation
}

/**
 * Immutable audit record for one enrollment-user activity qualification. The snapshot ensures a
 * later rule edit cannot reclassify the event, while status changes allow cancellation/refund
 * reconciliation without deleting history.
 */
export type FreeDropinQualificationRecord = {
  id: string
  clientId: string
  enrollmentUserId: string
  enrollmentId: string
  activityId: string
  periodKey: string
  ruleId: string
  ruleRevision: number
  ruleSnapshot: FreeDropinRuleSnapshot
  trigger: FreeDropinTicketTrigger
  qualifiedAt: string
  status: FreeDropinQualificationStatus
  disqualifiedAt?: string
  createdAt: string
  updatedAt: string
}

/** Prior granted total for a (rule, period, student), used to compute the delta on recalculation. */
export type FreeDropinGrantedRecord = {
  ruleId: string
  periodKey: string
  ticketsGranted: number
}

/**
 * Result of a recalculation for a (rule, period, student). `delta` is the signed change to apply:
 * > 0 grant that many, < 0 revoke that many, 0 no-op. This makes repeated recalculation idempotent
 * and naturally handles cancellation/refund shrinking the entitlement.
 */
export type FreeDropinGrant = {
  ruleId: string
  packageId: string
  periodKey: string
  qualifyingCount: number // qualifying registrations counted for this (rule, period)
  entitledTickets: number // recomputed total entitlement
  alreadyGranted: number // from the matching FreeDropinGrantedRecord (0 if none)
  delta: number // entitledTickets - alreadyGranted
}

/**
 * Persisted entitlement for one enrollment user, logical rule revision, and period. Entitlement is
 * separate from the ticket's physical count so consumed clips are never re-granted after shrink.
 */
export type FreeDropinGrantRecord = {
  id: string
  clientId: string
  enrollmentUserId: string
  ruleId: string
  ruleRevision: number
  periodKey: string
  packageId: string
  ticketId: string
  qualificationIds: string[]
  entitledTickets: number
  materializedTicketCount: number
  unrecoverableUsedClipShortfall: number
  status: FreeDropinGrantStatus
  createdAt: string
  updatedAt: string
}

export type FreeDropinTicketProvenance = {
  grantId: string
  ruleId: string
  ruleRevision: number
  periodKey: string
}

export enum FreeDropinRuleValidationErrorCode {
  ActivityInMultipleRules = 'ACTIVITY_IN_MULTIPLE_RULES',
  UnknownPackage = 'UNKNOWN_PACKAGE',
  InactivePackage = 'INACTIVE_PACKAGE',
  MultiClipPackage = 'MULTI_CLIP_PACKAGE',
  EmptyActivitySet = 'EMPTY_ACTIVITY_SET',
  InvalidThreshold = 'INVALID_THRESHOLD',
  DuplicateThresholdCount = 'DUPLICATE_THRESHOLD_COUNT',
  InvalidRuleRevision = 'INVALID_RULE_REVISION',
  DuplicateRuleRevision = 'DUPLICATE_RULE_REVISION'
}

export type FreeDropinRuleValidationError = {
  ruleId?: string
  activityId?: string
  code: FreeDropinRuleValidationErrorCode
  message: string
}
